import { IdName } from './../id-name.model';
import { ParsedAccessToken } from '.';
import { CustomUtils } from '../../utils/custom-utils';
import { CurrentProgramAuthDataModel } from './CurrentProgramAuthDataModel';
import { CurrentSiteAuthDataModel } from './CurrentSiteAuthDataModel';
import { AvailableProgramModel } from './AvailableProgramModel';
import { CADConnectionType, ProgramType, Tier } from '@app/shared/enums';
import { SystemRole } from 'src/app/shared/enums/system-role.enum';
import { SiteRole } from 'src/app/shared/enums/site-role.enum';
import { ProgramRole } from 'src/app/shared/enums/program-role.enum';
import { ProgramFilter } from 'src/app/shared/enums/program-filter.enum';

export class UserInfo {
  public static readonly empty: UserInfo = Object.assign(new UserInfo(), {
    AvailablePrograms: new Array<IdName>(),
    CurrentProgram: new CurrentProgramAuthDataModel(),
    CurrentSite: new CurrentSiteAuthDataModel(),
    SystemRoles: new Array<string>(),
    SiteRoles: new Array<IdName>(),
    ProgramRoles: new Array<IdName>()
  });

  private readonly ComplexClaimInlineSeparator: string = '|;';

  private readonly EMAIL_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';
  private readonly SID_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid';
  private readonly NAME_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';
  private readonly FIRSTNAME_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname';
  private readonly SURNAME_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname';

  public CurrentSiteId = 0;
  public CurrentProgramId: number | null = null;
  public CurrentProgramRoleId: string = null;
  public SalesForceId: string = null;

  public UserId = '';
  public Email = '';
  public UserName = '';
  public UserFirstname = '';
  public UserSurname = '';
  public AvatarSrc = '';
  public IsPasswordInitial = false;
  public GlobalUserId = '';
  public LocationRestriction = false;
  public LocationName = '';
  public IsSuperUserLevel1Enabled = false;
  public SiteAdminAuthorityDate = '';

  public CurrentProgram: CurrentProgramAuthDataModel;
  public CurrentSite: CurrentSiteAuthDataModel;

  public SystemRoles: SystemRole[] = [];
  public AvailablePrograms: AvailableProgramModel[] = [];
  public SiteRoles: IdName[] = [];
  public ProgramRoles: IdName[] = [];

  public get CurrentSiteRoles(): Array<SiteRole> {
    return this.SiteRoles.filter((x) => x.id === this.CurrentSiteId).map((x) => SiteRole[x.name] as SiteRole);
  }

  public get CurrentProgramRoles(): Array<ProgramRole> {
    return this.ProgramRoles.filter((x) => x.id === this.CurrentProgramId).map(
      (x) => ProgramRole[x.name] as ProgramRole
    );
  }

  public get IsSysAdmin(): boolean {
    if (this._isSysAdmin === undefined) {
      this._isSysAdmin = this.SystemRoles.includes(SystemRole.SystemAdministrator);
    }

    return this._isSysAdmin;
  }
  private _isSysAdmin: boolean;

  public get IsSiteAdmin(): boolean {
    if (this._isSiteAdmin === undefined) {
      this._isSiteAdmin = this.SiteRoles.some((r) => r.id === this.CurrentSiteId && r.name === 'SiteAdministrator');
    }

    return this._isSiteAdmin;
  }
  private _isSiteAdmin: boolean;

  public get IsObserver(): boolean {
    if (this._isObserver === undefined) {
      this._isObserver = this.SiteRoles.some((r) => r.id === this.CurrentSiteId && r.name === 'Observer');
    }

    return this._isObserver;
  }
  private _isObserver: boolean;

  public get IsTrainingCoordinator(): boolean {
    if (this._isTrainingCoordinator === undefined) {
      this._isTrainingCoordinator = this.CurrentSiteRoles.includes(SiteRole.TrainingCoordinator);
    }

    return this._isTrainingCoordinator;
  }
  private _isTrainingCoordinator: boolean;

  public get IsTrainee(): boolean {
    if (this._isTrainee === undefined) {
      this._isTrainee = this.isTraineeInCurrentProgram();
    }

    return this._isTrainee;
  }
  private _isTrainee: boolean;

  public get IsTrainer(): boolean {
    if (this._isTrainer === undefined) {
      this._isTrainer = this.isTrainerInCurrentProgram();
    }

    return this._isTrainer;
  }
  private _isTrainer: boolean;

  public get IsCoordinator(): boolean {
    if (this._isCoordinator === undefined) {
      this._isCoordinator = this.isCoordinatorInCurrentProgram();
    }

    return this._isCoordinator;
  }
  private _isCoordinator: boolean;

  public get IsSupervisor(): boolean {
    if (this._isSupervisor === undefined) {
      this._isSupervisor = this.isSupervisorInCurrentProgram();
    }

    return this._isSupervisor;
  }
  private _isSupervisor: boolean;

  public get hasProgramSettingsPermission(): boolean {
    return this.IsCoordinator || this.IsSiteAdmin || this.IsSysAdmin;
  }

  public get hasSiteSettingsPermission(): boolean {
    return this.IsSiteAdmin || this.IsSysAdmin;
  }

  public get hasTrainingCoursesPermission(): boolean {
    return this.IsTrainingCoordinator || this.IsSiteAdmin || this.IsSysAdmin;
  }

  public CurrentProgramName: string;

  constructor(parsedToken?: ParsedAccessToken) {
    if (parsedToken) {
      this.init(parsedToken);
    }
  }

  private init(parsedToken: ParsedAccessToken): void {
    this.CurrentSiteId = parsedToken.CurrentSiteId === '' ? null : +parsedToken.CurrentSiteId;
    this.CurrentProgramId = parsedToken.CurrentProgramId === '' ? null : +parsedToken.CurrentProgramId;
    this.CurrentProgramRoleId = parsedToken.CurrentProgramRoleId === '' ? null : parsedToken.CurrentProgramRoleId;
    this.SalesForceId = parsedToken.SalesForceId === '' ? null : parsedToken.SalesForceId;

    this.GlobalUserId = parsedToken.GlobalUserId === '' ? null : parsedToken.GlobalUserId;

    this.SystemRoles = CustomUtils.makeArray((parsedToken as ParsedAccessToken).SystemRoles).map((x) => SystemRole[x]);

    this.AvailablePrograms = CustomUtils.makeArray((parsedToken as ParsedAccessToken).AvailablePrograms).map(
      (data: string) => this.parseAvailableProgram(data)
    );

    this.SiteRoles = CustomUtils.makeArray((parsedToken as ParsedAccessToken).SiteRoles).map((data: string) =>
      this.parseSiteRoles(data)
    );

    this.ProgramRoles = CustomUtils.makeArray((parsedToken as ParsedAccessToken).ProgramRoles).map((data: string) =>
      this.parseProgramRoles(data)
    );

    if (parsedToken.CurrentSiteData !== '') {
      this.CurrentSite = this.parseCurrentSite(parsedToken.CurrentSiteData);
    }

    if (parsedToken.CurrentProgramData !== '') {
      this.CurrentProgram = this.parseCurrentProgram(parsedToken.CurrentProgramData);
    }

    this.Email = parsedToken[this.EMAIL_CLAIM];
    this.UserId = parsedToken[this.SID_CLAIM];
    this.UserName = parsedToken[this.NAME_CLAIM];
    this.UserSurname = parsedToken[this.SURNAME_CLAIM];
    this.UserFirstname = parsedToken[this.FIRSTNAME_CLAIM];
    this.AvatarSrc = parsedToken.AvatarSrc;
    this.IsPasswordInitial = parsedToken.IsPasswordInitial;
    this.LocationRestriction = parsedToken.LocationRestriction;
    this.LocationName = parsedToken.LocationName;
    this.IsSuperUserLevel1Enabled = parsedToken.SuperUserLevel1Enabled;
    this.SiteAdminAuthorityDate = parsedToken.SiteAdminAuthorityDate;
  }

  private isTraineeInCurrentProgram(): boolean {
    return this.ProgramRoles.some((r) => r.id === this.CurrentProgramId && r.name === 'Trainee');
  }

  private isTrainerInCurrentProgram(): boolean {
    return this.ProgramRoles.some((r) => r.id === this.CurrentProgramId && r.name === 'Trainer');
  }

  private isSupervisorInCurrentProgram(): boolean {
    return this.ProgramRoles.some((r) => r.id === this.CurrentProgramId && r.name === 'Supervisor');
  }

  private isCoordinatorInCurrentProgram(): boolean {
    return this.ProgramRoles.some((r) => r.id === this.CurrentProgramId && r.name === 'Coordinator');
  }

  private parseProgramRoles(data: string): IdName {
    const splited = (data as string).split(this.ComplexClaimInlineSeparator);

    return new IdName(parseInt(splited[0]), splited[1]);
  }

  private parseSiteRoles(data: string): IdName {
    const splited = (data as string).split(this.ComplexClaimInlineSeparator);

    return new IdName(parseInt(splited[0]), splited[1]);
  }

  private parseAvailableProgram(data: string): AvailableProgramModel {
    const splited = (data as string).split(this.ComplexClaimInlineSeparator);

    return new AvailableProgramModel(parseInt(splited[0]), splited[1], ProgramType[splited[2]]);
  }

  private parseCurrentSite(data: string): CurrentSiteAuthDataModel {
    if (!data) {
      return null;
    }

    const splitted = data.split(this.ComplexClaimInlineSeparator);

    return {
      Tier: Tier[splitted[0]],
      MaxUploadSize: +splitted[1],
      Name: splitted[2],
      Guid: splitted[3],
      MaxStorageSize: splitted[4],
      LogoSrc: splitted[5] ? splitted[5] : null,
      TimeZoneId: splitted[6] ? splitted[6] : null,
      MentoringEnabled: JSON.parse(splitted[7]?.toLocaleLowerCase() ?? null),
      PowerDmsEnabled: JSON.parse(splitted[8]?.toLocaleLowerCase() ?? null),
      TrainingCoursesEnabled: JSON.parse(splitted[9]?.toLocaleLowerCase() ?? null),
      CadImportEnabled: JSON.parse(splitted[10]?.toLocaleLowerCase() ?? null),
      FoundationUserManagementEnabled: JSON.parse(splitted[11]?.toLocaleLowerCase() ?? null),
      PowerSuitEidAuthEnabled: JSON.parse(splitted[12]?.toLocaleLowerCase() ?? null),
      SecureSignInEnabled: JSON.parse(splitted[13]?.toLocaleLowerCase() ?? null),
      GlobalSiteId: splitted[14] ? splitted[14] : null,
      RaceAndSexReportingEnabled: JSON.parse(splitted[16]?.toLocaleLowerCase() ?? null),
      UserwayEnabled: JSON.parse(splitted[17]?.toLocaleLowerCase() ?? null),
      PlatformTopNavEnabled: JSON.parse(splitted[18]?.toLocaleLowerCase() ?? null),
      CADConnectionType: CADConnectionType[splitted[19]] || CADConnectionType.None,
      SyncPlatformExtendedUserFieldsEnabled: JSON.parse(splitted[20]?.toLocaleLowerCase() ?? null),
      PlatformTasksEnabled: JSON.parse(splitted[21]?.toLocaleLowerCase() ?? null),
      AllowAiWritingAssistant: JSON.parse(splitted[22]?.toLocaleLowerCase() ?? 'true'),
      PipEnabled: JSON.parse(splitted[23]?.toLocaleLowerCase() ?? 'false'),
      RatingScaleUpdateEnabled: JSON.parse(splitted[24]?.toLocaleLowerCase() ?? 'false'),
      AssignPolicyDocumentsEnabled: JSON.parse(splitted[25]?.toLocaleLowerCase() ?? 'false'),
      AITraineeSummaryEnabled: JSON.parse(splitted[26]?.toLocaleLowerCase() ?? 'false'),
      PowerEngageIntegrationEnabled: JSON.parse(splitted[27]?.toLocaleLowerCase() ?? 'false')
    };
  }

  private parseCurrentProgram(data: string): CurrentProgramAuthDataModel {
    if (!data) {
      return null;
    }

    const splitted = data.split(this.ComplexClaimInlineSeparator);

    this.CurrentProgramName = this.AvailablePrograms.find((x) => x.Id === this.CurrentProgramId)?.Name;

    return {
      Type: ProgramType[splitted[0]],
      Active: splitted[1] === 'true',
      FilterTrainersByLocation: ProgramFilter[splitted[2]],
      FilterTrainersByShift: ProgramFilter[splitted[3]],
      FilterSupervisorsByLocation: ProgramFilter[splitted[4]],
      FilterSupervisorsByShift: ProgramFilter[splitted[5]],
      EnableNRT: splitted[6] === 'True',
      DisplayPowerRecallInsights: JSON.parse(splitted[7]?.toLocaleLowerCase() ?? 'false'),
      EnableAiWritingAssistant: JSON.parse(splitted[8]?.toLocaleLowerCase() ?? 'false'),
      AITraineeSummaryEnabled: JSON.parse(splitted[9]?.toLocaleLowerCase() ?? 'false')
    };
  }
}
